import { supabase } from './supabaseClient.js'

class SupabaseProfileService {
    // 获取用户资料
    async getUserProfile(userId) {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('user_id', userId)
                .single()

            if (error) throw error
            return data
        } catch (error) {
            console.error('获取用户资料失败:', error)
            throw new Error('获取用户资料失败')
        }
    }

    // 更新用户基本信息
    async updateProfile(userId, updates) {
        try {
            // 检查三天更新限制
            const profile = await this.getUserProfile(userId)
            const lastUpdate = new Date(profile.updated_at)
            const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)

            if (lastUpdate > threeDaysAgo) {
                const remainingTime = Math.ceil((lastUpdate.getTime() + 3 * 24 * 60 * 60 * 1000 - Date.now()) / (24 * 60 * 60 * 1000))
                throw new Error(`基本信息每3天只能更新一次，还需等待 ${remainingTime} 天`)
            }

            const { data, error } = await supabase
                .from('profiles')
                .update({
                    ...updates,
                    updated_at: new Date().toISOString()
                })
                .eq('user_id', userId)
                .select()
                .single()

            if (error) throw error
            return data
        } catch (error) {
            console.error('更新用户资料失败:', error)
            throw error
        }
    }

    // 上传头像
    async uploadAvatar(userId, file) {
        try {
            // 生成唯一文件名
            const fileExt = file.name.split('.').pop()
            const fileName = `${userId}-${Date.now()}.${fileExt}`
            const filePath = `avatars/${fileName}`

            // 上传文件到 Supabase Storage
            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('mahirublog')
                .upload(filePath, file, {
                    cacheControl: '3600',
                    upsert: false
                })

            if (uploadError) throw uploadError

            // 获取公共URL
            const { data: { publicUrl } } = supabase.storage
                .from('mahirublog')
                .getPublicUrl(filePath)

            // 更新用户资料中的头像URL
            const { data, error } = await supabase
                .from('profiles')
                .update({ avatar_url: publicUrl })
                .eq('user_id', userId)
                .select()
                .single()

            if (error) throw error
            return data
        } catch (error) {
            console.error('上传头像失败:', error)
            throw new Error('上传头像失败')
        }
    }

    // 获取用户评论
    async getUserComments(userId, page = 1, limit = 10) {
        try {
            const offset = (page - 1) * limit

            const { data, error } = await supabase
                .from('comments')
                .select(`
          id,
          content,
          created_at,
          article_id,
          articles!inner(
            id,
            title
          )
        `)
                .eq('user_id', userId)
                .order('created_at', { ascending: false })
                .range(offset, offset + limit - 1)

            if (error) throw error
            return data
        } catch (error) {
            console.error('获取用户评论失败:', error)
            throw new Error('获取用户评论失败')
        }
    }

    // 获取用户评论总数
    async getUserCommentsCount(userId) {
        try {
            const { count, error } = await supabase
                .from('comments')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', userId)

            if (error) throw error
            return count
        } catch (error) {
            console.error('获取用户评论总数失败:', error)
            throw new Error('获取用户评论总数失败')
        }
    }

    // 删除用户评论
    async deleteComment(commentId, userId) {
        try {
            const { error } = await supabase
                .from('comments')
                .delete()
                .eq('id', commentId)
                .eq('user_id', userId) // 确保只能删除自己的评论

            if (error) throw error
            return true
        } catch (error) {
            console.error('删除评论失败:', error)
            throw new Error('删除评论失败')
        }
    }

    // 检查用户名是否可用（排除当前用户）
    async checkUsernameAvailable(username, currentUserId) {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('user_id')
                .eq('username', username)
                .neq('user_id', currentUserId)
                .maybeSingle()

            if (error) throw error
            return !data // 如果没有找到记录，说明用户名可用
        } catch (error) {
            console.error('检查用户名可用性失败:', error)
            throw new Error('检查用户名可用性失败')
        }
    }

    // 获取当前认证用户
    async getCurrentUser() {
        try {
            const { data: { user }, error } = await supabase.auth.getUser()
            if (error) throw error
            return user
        } catch (error) {
            console.error('获取当前用户失败:', error)
            throw new Error('获取当前用户失败')
        }
    }

    // === 管理员功能 ===

    // 获取所有用户列表（分页）- 基本信息
    async getAllUsers(page = 1, limit = 20, searchQuery = '') {
        try {
            const offset = (page - 1) * limit

            let query = supabase
                .from('profiles')
                .select(`
                    user_id,
                    username,
                    display_name,
                    email,
                    avatar_url,
                    role,
                    created_at,
                    updated_at
                `)
                .order('created_at', { ascending: false })

            // 搜索功能
            if (searchQuery) {
                query = query.or(`username.ilike.%${searchQuery}%,display_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`)
            }

            const { data, error } = await query
                .range(offset, offset + limit - 1)

            if (error) throw error
            return data || []
        } catch (error) {
            console.error('获取用户列表失败:', error)
            throw new Error('获取用户列表失败')
        }
    }

    // 获取用户总数
    async getUsersCount(searchQuery = '') {
        try {
            let query = supabase
                .from('profiles')
                .select('*', { count: 'exact', head: true })

            if (searchQuery) {
                query = query.or(`username.ilike.%${searchQuery}%,display_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`)
            }

            const { count, error } = await query

            if (error) throw error
            return count || 0
        } catch (error) {
            console.error('获取用户总数失败:', error)
            throw new Error('获取用户总数失败')
        }
    }

    // 获取用户注册统计（按日期）
    async getUserRegistrationStats(days = 30) {
        try {
            const endDate = new Date()
            const startDate = new Date(endDate.getTime() - days * 24 * 60 * 60 * 1000)

            const { data, error } = await supabase
                .from('profiles')
                .select('created_at')
                .gte('created_at', startDate.toISOString())
                .lte('created_at', endDate.toISOString())
                .order('created_at', { ascending: true })

            if (error) throw error

            // 按日期分组统计
            const stats = {}
            const today = new Date()

            // 初始化所有日期为0
            for (let i = 0; i < days; i++) {
                const date = new Date(today.getTime() - i * 24 * 60 * 60 * 1000)
                const dateStr = date.toISOString().split('T')[0]
                stats[dateStr] = 0
            }

            // 统计实际注册数
            data.forEach(user => {
                const dateStr = user.created_at.split('T')[0]
                if (stats.hasOwnProperty(dateStr)) {
                    stats[dateStr]++
                }
            })

            // 转换为数组格式
            return Object.entries(stats)
                .map(([date, count]) => ({ date, count }))
                .sort((a, b) => new Date(a.date) - new Date(b.date))
        } catch (error) {
            console.error('获取用户注册统计失败:', error)
            throw new Error('获取用户注册统计失败')
        }
    }

    // 获取月度用户注册统计
    async getMonthlyUserStats(months = 12) {
        try {
            const endDate = new Date()
            const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - months + 1, 1)

            const { data, error } = await supabase
                .from('profiles')
                .select('created_at')
                .gte('created_at', startDate.toISOString())
                .lte('created_at', endDate.toISOString())
                .order('created_at', { ascending: true })

            if (error) throw error

            // 按月份分组统计
            const stats = {}
            const today = new Date()

            // 初始化所有月份为0
            for (let i = 0; i < months; i++) {
                const date = new Date(today.getFullYear(), today.getMonth() - i, 1)
                const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
                stats[monthStr] = 0
            }

            // 统计实际注册数
            data.forEach(user => {
                const date = new Date(user.created_at)
                const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
                if (stats.hasOwnProperty(monthStr)) {
                    stats[monthStr]++
                }
            })

            // 转换为数组格式
            return Object.entries(stats)
                .map(([month, count]) => ({ month, count }))
                .sort((a, b) => new Date(a.month) - new Date(b.month))
        } catch (error) {
            console.error('获取月度用户统计失败:', error)
            throw new Error('获取月度用户统计失败')
        }
    }

    // 获取用户详细信息（包括评论数）
    async getUserDetails(userId) {
        try {
            // 获取用户profile信息
            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('*')
                .eq('user_id', userId)
                .single()

            if (profileError) throw profileError

            // 获取用户评论数
            const { count: commentsCount, error: commentsError } = await supabase
                .from('comments')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', userId)

            if (commentsError) throw commentsError

            return {
                ...profile,
                comments_count: commentsCount || 0
            }
        } catch (error) {
            console.error('获取用户详细信息失败:', error)
            throw new Error('获取用户详细信息失败')
        }
    }
}

export default new SupabaseProfileService() 